import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useHistory, useLocation } from "react-router-dom";
import Head from "../../../layout/head/Head";
import ContentAlt from "../../../layout/content/ContentAlt";
import {
  serviceGetLivechatConversation,
  serviceGetLivechatConversations,
  serviceGetLivechatMessages,
  serviceGetLivechatNotes,
  serviceGetLivechatTickets,
  serviceGetLivechatUploadLimits,
  serviceDeleteLivechatMessage,
  servicePostLivechatRead,
  servicePutLivechatOpen,
  servicePutLivechatStatus,
} from "../../../services/livechat";
import {
  accessOf,
  canSee,
  forRole,
  initialState,
  livechatReducer,
  matchesFilters,
  PERMISSION_KEY,
  readFieldsOf,
  unreadVisitorSeq,
} from "./livechatState";
import { useLivechatOptions } from "./useLivechatOptions";
import { useLivechatRealtime } from "./useLivechatRealtime";
import { useLivechatSender } from "./useLivechatSender";
import { acceptOf, FALLBACK_LIMITS } from "./livechatUpload";
import { useLivechatTyping } from "./useLivechatTyping";
import Swal from "sweetalert2";
import LivechatAside from "./LivechatAside";
import LivechatBody from "./LivechatBody";
import LivechatContact from "./LivechatContact";
import { Icon } from "../../../components/Component";
import "./livechat.css";

const OPEN_DEBOUNCE_MS = 500;
const ENDED_STATUSES = new Set(["RESOLVED", "SOLVED"]);
const RESOLVER_ROLES = new Set(["SALES", "CS"]);
const STATUS_ERRORS = {
  "error.livechat.solve_forbidden": "Hanya Sales atau CS yang bisa menandai SOLVED.",
  "error.livechat.ticket_already_open": "Pengunjung sudah menulis lagi, tiket baru sudah terbuka.",
  "error.livechat.ticket_closed": "Tiket sudah ditutup.",
  "error.livechat.no_ticket": "Percakapan ini belum punya tiket.",
  "error.livechat.ticket_resolved": "Tiket yang sudah Resolve tidak bisa dibuka kembali.",
};
const PROFILE_INLINE_MIN_PX = 1550;
const MAX_NOTES_PER_TICKET = 500;

const toIds = (raw) =>
  Array.isArray(raw)
    ? raw.filter(Boolean)
    : String(raw ?? "")
        .split(",")
        .filter(Boolean);

const BlankState = ({ message }) => (
  <div className="nk-chat-body">
    <div className="nk-chat-blank">
      <div className="nk-chat-blank-icon">
        <Icon name="chat" className="icon-circle icon-circle-xxl bg-white" />
      </div>
      <div className="nk-chat-blank-btn">
        <h6>{message}</h6>
      </div>
    </div>
  </div>
);

const LivechatInner = ({ permissions }) => {
  const [cookies] = useCookies(["SIDikey", "id", "departmentIds", "role"]);
  const token = cookies.SIDikey;
  const me = useMemo(
    () => ({
      id: cookies.id,
      departmentIds: toIds(cookies.departmentIds),
      access: accessOf(permissions),
      readFields: readFieldsOf(cookies.role?.name),
    }),
    [cookies.id, String(cookies.departmentIds ?? ""), permissions, cookies.role?.name], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const options = useLivechatOptions(token, me);
  const history = useHistory();
  const location = useLocation();
  const selectedId = new URLSearchParams(location.search).get("c");

  const [state, dispatch] = useReducer(livechatReducer, initialState);
  const [filters, setFilters] = useState({});
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [notes, setNotes] = useState([]);
  const [blocked, setBlocked] = useState(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(window.innerWidth > PROFILE_INLINE_MIN_PX);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const lastTicketId = state.messages.length ? state.messages[state.messages.length - 1].ticket_id : undefined;
  const sender = useLivechatSender({ token, conversationId: selectedId, lastTicketId, dispatch });
  const [visitorTyping, setVisitorTyping] = useState(null);
  const typingTimerRef = useRef(null);
  const lastReadSeqRef = useRef({});
  const { reopenComposer } = sender;

  const listRequestRef = useRef(0);
  const notedTicketsRef = useRef(new Set());
  const openTimerRef = useRef(null);
  const isCurrent = (conversationId) => selectedIdRef.current === conversationId;

  useEffect(() => () => clearTimeout(openTimerRef.current), []);

  const loadList = useCallback(
    async (cursor) => {
      listRequestRef.current += 1;
      const requestId = listRequestRef.current;
      setIsLoadingList(true);
      const res = await serviceGetLivechatConversations(token, { ...filters, cursor });

      // a newer request (other filters) owns the list now
      if (requestId !== listRequestRef.current) return;

      setIsLoadingList(false);

      if (res.status === 200) {
        dispatch({
          type: cursor ? "list/append" : "list/replace",
          items: res.data.data,
          nextCursor: res.data.nextCursor,
        });
      }
    },
    [token, filters],
  );

  const loadTicketNotes = useCallback(
    async (conversationId, ticketId) => {
      const notesOfTicket = [];
      let cursor;

      do {
        const res = await serviceGetLivechatNotes(token, conversationId, ticketId, cursor);

        if (res.status !== 200) break;

        notesOfTicket.push(...(res.data.notes ?? []).map((note) => ({ ...note, ticket_id: ticketId })));
        cursor = res.data.next_cursor;
      } while (cursor && notesOfTicket.length < MAX_NOTES_PER_TICKET);

      return notesOfTicket;
    },
    [token],
  );

  // notes of tickets not loaded yet (older pages, a new ticket by socket)
  const loadNotes = useCallback(
    async (conversationId, messages) => {
      const ticketIds = [...new Set(messages.map((message) => message.ticket_id))].filter(
        (ticketId) => ticketId && !notedTicketsRef.current.has(ticketId),
      );

      if (ticketIds.length === 0) return;

      ticketIds.forEach((ticketId) => notedTicketsRef.current.add(ticketId));
      const pages = await Promise.all(ticketIds.map((ticketId) => loadTicketNotes(conversationId, ticketId)));

      if (!isCurrent(conversationId)) return;

      setNotes((prev) => [...new Map([...prev, ...pages.flat()].map((note) => [note.id, note])).values()]);
    },
    [loadTicketNotes],
  );

  const loadMessages = useCallback(
    async (conversationId, mode = "replace") => {
      const res = await serviceGetLivechatMessages(token, conversationId);

      if (res.status !== 200 || !isCurrent(conversationId)) return;

      dispatch(
        mode === "replace"
          ? { type: "messages/replace", messages: res.data.messages, nextBefore: res.data.next_before }
          : { type: "messages/merge", messages: res.data.messages },
      );
      loadNotes(conversationId, res.data.messages);
    },
    [token, loadNotes],
  );

  const loadOlder = useCallback(async () => {
    const conversationId = selectedId;

    if (!conversationId || !state.nextBefore) return;

    const res = await serviceGetLivechatMessages(token, conversationId, state.nextBefore);

    if (res.status !== 200 || !isCurrent(conversationId)) return;

    dispatch({ type: "messages/prepend", messages: res.data.messages, nextBefore: res.data.next_before });
    loadNotes(conversationId, res.data.messages);
  }, [token, selectedId, state.nextBefore, loadNotes]);

  // debounced: a burst of visitor messages sends one request
  const markOpened = useCallback(
    (conversationId) => {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = setTimeout(async () => {
        const res = await servicePutLivechatOpen(token, conversationId);

        if (res.status === 200) dispatch({ type: "conversation/upsert", conversation: res.data });
      }, OPEN_DEBOUNCE_MS);
    },
    [token],
  );

  const loadConversation = useCallback(
    async (conversationId) => {
      setBlocked(null);
      setDetail(null);
      setNotes([]);
      setTickets([]);
      notedTicketsRef.current = new Set();
      dispatch({ type: "messages/replace", messages: [], nextBefore: null });
      const res = await serviceGetLivechatConversation(token, conversationId);

      if (!isCurrent(conversationId)) return;
      if (res.status === 403) return setBlocked("Anda tidak punya akses ke percakapan ini.");
      if (res.status !== 200) return setBlocked("Percakapan tidak ditemukan.");

      setDetail(res.data);
      loadMessages(conversationId);
      serviceGetLivechatTickets(token, conversationId).then((ticketsRes) => {
        if (ticketsRes.status === 200 && isCurrent(conversationId)) setTickets(ticketsRes.data);
      });
      markOpened(conversationId);
    },
    [token, loadMessages, markOpened],
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) loadConversation(selectedId);
  }, [selectedId, loadConversation]);

  const listRef = useRef(state.conversations);
  listRef.current = state.conversations;

  const handleEvent = useCallback(
    async (event) => {
      if (event.note && isCurrent(event.conversationId)) {
        setNotes((prev) => (prev.some((note) => note.id === event.note.id) ? prev : [...prev, event.note]));
      }

      // the visitor is typing: shown until expiresAt, about 5 seconds
      if (event.event === "conversation.typing") {
        if (!isCurrent(event.conversationId)) return;

        clearTimeout(typingTimerRef.current);
        const msLeft = event.typing ? new Date(event.expiresAt) - Date.now() : 0;

        if (msLeft <= 0) {
          setVisitorTyping(null);
          return;
        }

        setVisitorTyping(event.conversationId);
        typingTimerRef.current = setTimeout(() => setVisitorTyping(null), msLeft);
        return;
      }

      if (event.event === "conversation.unassigned") {
        dispatch({ type: "conversation/remove", conversationId: event.conversationId });
        if (isCurrent(event.conversationId)) setBlocked("Percakapan ini sudah dipindahkan.");
        return;
      }

      const conversation = forRole(event.conversation, me.readFields);

      if (!conversation) return;

      if (!canSee(conversation, me)) {
        dispatch({ type: "conversation/remove", conversationId: conversation.conversationId });
        return;
      }

      const isListed = listRef.current.some((item) => item.conversationId === conversation.conversationId);

      if (isListed) {
        dispatch({ type: "conversation/upsert", conversation });
      } else if (matchesFilters(conversation, filters)) {
        const res = await serviceGetLivechatConversation(token, conversation.conversationId);
        if (res.status === 200) dispatch({ type: "conversation/upsert", conversation: res.data.conversation });
      }

      if (!isCurrent(conversation.conversationId) || !event.message) return;

      dispatch({ type: "messages/upsert", message: event.message });

      // message.read only updates the ticks of a message already shown
      if (event.event !== "message.created") return;

      loadNotes(conversation.conversationId, [event.message]);

      if (event.message.sender?.type === "user") {
        clearTimeout(typingTimerRef.current);
        setVisitorTyping(null);
      }

      // a visitor message opens a new ticket, so replies work again
      if (event.message.sender?.type === "user") reopenComposer();

      const isUnread = conversation.unreadCount > 0 || conversation.status === "UNOPENED";

      // only while the agent is looking: a chat left open in a hidden tab stays unopened
      if (isUnread && conversation.userId === me.id && document.visibilityState === "visible") {
        markOpened(conversation.conversationId);
      }
    },
    [me, filters, token, markOpened, loadNotes, reopenComposer],
  );

  // keeps older pages the user already scrolled through
  const handleReconnect = useCallback(() => {
    loadList();
    if (selectedIdRef.current) loadMessages(selectedIdRef.current, "merge");
  }, [loadList, loadMessages]);

  useLivechatRealtime(handleEvent, handleReconnect);

  const selectConversation = (conversationId) => history.push({ search: `?c=${conversationId}` });

  const handleAssigned = (conversation) => {
    if (canSee(conversation, me)) {
      dispatch({ type: "conversation/upsert", conversation });
      setDetail((prev) => (prev ? { ...prev, conversation: { ...prev.conversation, ...conversation } } : prev));
      return;
    }

    dispatch({ type: "conversation/remove", conversationId: conversation.conversationId });
    history.push({ search: "" });
  };

  const conversations = useMemo(
    () => state.conversations.map((item) => forRole(item, me.readFields)),
    [state.conversations, me.readFields],
  );
  const selected =
    conversations.find((item) => item.conversationId === selectedId) ?? forRole(detail?.conversation, me.readFields);

  const isHolder = Boolean(selected) && selected.userId === me.id;
  const typing = useLivechatTyping(token, selectedId, isHolder);
  const [uploadLimits, setUploadLimits] = useState(FALLBACK_LIMITS);

  // only the holder sends files; Live Chat's settings of the conversation's site
  useEffect(() => {
    if (!isHolder || !selectedId) return undefined;

    let isStale = false;
    serviceGetLivechatUploadLimits(token, selectedId).then((res) => {
      if (!isStale) setUploadLimits(res?.status === 200 ? res.data : FALLBACK_LIMITS);
    });

    return () => {
      isStale = true;
    };
  }, [isHolder, selectedId, token]);

  // ✓✓ for the visitor once their holder has seen the messages, tab in view
  useEffect(() => {
    if (!isHolder || !selectedId) return undefined;

    const sendRead = () => {
      if (document.visibilityState !== "visible") return;

      const throughSeq = unreadVisitorSeq(state.messages, selectedId);

      if (throughSeq <= (lastReadSeqRef.current[selectedId] ?? 0)) return;

      lastReadSeqRef.current[selectedId] = throughSeq;
      servicePostLivechatRead(token, selectedId, throughSeq);
    };

    sendRead();
    document.addEventListener("visibilitychange", sendRead);

    return () => document.removeEventListener("visibilitychange", sendRead);
  }, [isHolder, selectedId, state.messages, token]);

  // irreversible for the visitor too, so it is confirmed first
  const unsendMessage = async (message) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "Tarik pesan ini?",
      text: "Pengunjung akan melihat \"Pesan ditarik\".",
      showCancelButton: true,
      confirmButtonText: "Tarik",
      cancelButtonText: "Batal",
    });

    if (!isConfirmed) return;

    const res = await serviceDeleteLivechatMessage(token, message.conversation_id, message.seq);

    if (res.status === 200) {
      dispatch({ type: "messages/upsert", message: res.data });
      return;
    }

    const text =
      res.data?.message === "error.livechat.not_own_message" ? "Hanya pesan Anda sendiri yang bisa ditarik." : "Coba lagi.";
    Swal.fire({ icon: "error", title: "Gagal menarik pesan", text });
  };

  const profile = { isOpen: isProfileOpen, onToggle: () => setIsProfileOpen((open) => !open) };
  const changeStatus = async (status) => {
    if (status === "SOLVED") {
      const answer = await Swal.fire({
        icon: "question",
        title: "Tandai selesai?",
        text: "Pengunjung akan melihat percakapan ditandai selesai.",
        showCancelButton: true,
        confirmButtonText: "Ya, Solve",
        cancelButtonText: "Batal",
      });

      if (!answer.isConfirmed) return;
    }

    const conversationId = selectedId;
    setIsChangingStatus(true);
    const res = await servicePutLivechatStatus(token, conversationId, status);
    setIsChangingStatus(false);

    if (res.status === 200) {
      dispatch({ type: "conversation/upsert", conversation: res.data });

      // the agent may have opened another conversation meanwhile
      if (!isCurrent(conversationId)) return;

      setDetail((prev) => (prev ? { ...prev, conversation: { ...prev.conversation, ...res.data } } : prev));
      if (status === "OPENED") reopenComposer();
      return;
    }

    Swal.fire({
      icon: res.status === 409 ? "info" : "error",
      title: "Status tidak berubah",
      text: STATUS_ERRORS[res.data?.message] ?? "Coba lagi.",
    });
  };

  const ticketActions = {
    isEnded: ENDED_STATUSES.has(selected?.status),
    // a sale never reopens
    isResolved: selected?.status === "RESOLVED",
    // as on chats, only sales and CS mark a chat SOLVED
    canSolve: RESOLVER_ROLES.has(cookies.role?.name),
    // the holding sales or CS records the sale, as on WhatsApp chats
    canResolve:
      Boolean(detail?.ticket?.id) &&
      selected?.userId === me.id &&
      RESOLVER_ROLES.has(cookies.role?.name) &&
      !ENDED_STATUSES.has(selected?.status),
    contact: detail?.conversation?.contact,
    onResolved: (conversation) => {
      dispatch({ type: "conversation/upsert", conversation });
      setDetail((prev) =>
        prev && prev.conversation.conversationId === conversation.conversationId
          ? { ...prev, conversation: { ...prev.conversation, ...conversation }, ticket: null }
          : prev,
      );
    },
    isBusy: isChangingStatus,
    tags: options.tags,
    currentTagIds: (detail?.ticket?.tags ?? []).map((tag) => tag.id),
    onChange: changeStatus,
    onTagsSaved: (tags) =>
      setDetail((prev) =>
        prev && prev.conversation.conversationId === selectedId
          ? { ...prev, ticket: { ...(prev.ticket ?? {}), tags } }
          : prev,
      ),
  };

  const composer = {
    // only the holder replies, as on WhatsApp chats
    canReply: isHolder,
    isDisabled: sender.isTicketClosed || ENDED_STATUSES.has(selected?.status),
    isVisitorTyping: visitorTyping === selectedId,
    myId: me.id,
    unsend: unsendMessage,
    onTyping: typing.type,
    send: (text, replyTo) => {
      typing.stop();
      sender.send(text, replyTo);
    },
    resend: sender.resend,
    accept: acceptOf(uploadLimits),
    sendFile: (file, caption) => sender.sendFile(file, uploadLimits, caption),
  };
  const closeConversation = () => history.push({ search: "" });

  return (
    <div className="nk-chat lc-root">
      <LivechatAside
        conversations={conversations}
        hasMore={Boolean(state.nextCursor)}
        isLoading={isLoadingList}
        selectedId={selectedId}
        me={me}
        filters={filters}
        options={options}
        onFiltersChange={setFilters}
        onLoadMore={() => loadList(state.nextCursor)}
        onSelect={selectConversation}
      />
      {blocked ? (
        <BlankState message={blocked} />
      ) : selectedId && detail ? (
        <>
          <LivechatBody
            conversation={selected}
            messages={state.messages}
            notes={notes}
            ticket={detail.ticket}
            hasOlder={Boolean(state.nextBefore)}
            options={options}
            profile={profile}
            onLoadOlder={loadOlder}
            onAssigned={handleAssigned}
            composer={composer}
            ticketActions={ticketActions}
            onBack={closeConversation}
          />
          <LivechatContact
            conversation={selected}
            contact={detail.conversation.contact}
            messages={state.messages}
            tickets={tickets}
            options={options}
            profile={profile}
          />
        </>
      ) : (
        <BlankState message={selectedId ? "Memuat percakapan..." : "Pilih percakapan untuk mulai membalas"} />
      )}
    </div>
  );
};

const Livechat = () => {
  const permissions = useMemo(() => JSON.parse(localStorage.getItem("permission") || "{}"), []);

  return (
    <>
      <Head title="Live Chat Platform" />
      <ContentAlt>
        {permissions[PERMISSION_KEY] ? (
          <LivechatInner permissions={permissions} />
        ) : (
          <div className="nk-chat lc-root">
            <BlankState message="Anda tidak punya akses ke menu Live Chat Platform." />
          </div>
        )}
      </ContentAlt>
    </>
  );
};

export default Livechat;
